import { z } from "zod";
import type { TipoFinanceiro } from "./lancamento";

export const LINHAS_DO_DRE = [
  "RECEITA_OPERACIONAL",
  "DESCONTO",
  "DEDUCAO",
  "CUSTO_DO_SERVICO",
  "PESSOAL",
  "OCUPACAO",
  "ADMINISTRATIVA",
  "COMERCIAL",
  "PERDAS_COM_CLIENTES",
  "RESULTADO_FINANCEIRO",
  "IMPOSTO_SOBRE_O_LUCRO",
  "FORA_DO_RESULTADO",
] as const;
export type LinhaDoDre = (typeof LINHAS_DO_DRE)[number];

export const LINHA_DO_DRE: Record<LinhaDoDre, { rotulo: string; dica: string }> = {
  RECEITA_OPERACIONAL: { rotulo: "Receita", dica: "O que o laboratório fatura com o trabalho." },
  DESCONTO: { rotulo: "Descontos concedidos", dica: "Desconto ou abatimento dado ao cliente: sai da receita antes dos impostos." },
  DEDUCAO: { rotulo: "Impostos sobre o faturamento", dica: "DAS do Simples, ISS: sai antes de tudo, sobre o que foi faturado." },
  CUSTO_DO_SERVICO: { rotulo: "Custo dos serviços", dica: "Material e serviços de terceiros que entram no trabalho." },
  PESSOAL: { rotulo: "Pessoal", dica: "Salários, encargos e pró-labore." },
  OCUPACAO: { rotulo: "Ocupação e equipamentos", dica: "Aluguel, energia, água, manutenção." },
  ADMINISTRATIVA: { rotulo: "Administrativas", dica: "Contador, sistemas, escritório." },
  COMERCIAL: { rotulo: "Comerciais e entrega", dica: "Frete, motoboy, divulgação." },
  PERDAS_COM_CLIENTES: { rotulo: "Perdas com clientes", dica: "Valores a receber dados como perdidos." },
  RESULTADO_FINANCEIRO: { rotulo: "Resultado financeiro", dica: "Taxas de cartão e boleto, juros, tarifas e rendimentos." },
  IMPOSTO_SOBRE_O_LUCRO: { rotulo: "IR e CSLL", dica: "Só fora do Simples (no Simples já estão no DAS)." },
  FORA_DO_RESULTADO: { rotulo: "Fora do resultado", dica: "Mexe no caixa, não no resultado: retirada dos sócios, compra de equipamento, empréstimo." },
};

/** Linhas que cada tipo de categoria aceita. */
const DE_ENTRADA: readonly LinhaDoDre[] = ["RECEITA_OPERACIONAL", "RESULTADO_FINANCEIRO", "FORA_DO_RESULTADO"];

export function linhasDoTipo(tipo: TipoFinanceiro): LinhaDoDre[] {
  return tipo === "ENTRADA" ? [...DE_ENTRADA] : LINHAS_DO_DRE.filter((l) => l !== "RECEITA_OPERACIONAL");
}

export const linhaAceitaNoTipo = (tipo: TipoFinanceiro, linha: LinhaDoDre) => linhasDoTipo(tipo).includes(linha);

/** Natureza (fixo/variável) só importa onde entra na margem: fora do resultado e dedução não têm. */
export const linhaTemNatureza = (linha: LinhaDoDre | null) => linha !== "FORA_DO_RESULTADO" && linha !== "DEDUCAO" && linha !== "DESCONTO" && linha !== "RECEITA_OPERACIONAL";

/** Categoria de sistema: só as perdas dadas no A receber caem nela, nunca um lançamento à mão. */
export const CATEGORIA_DAS_PERDAS = "Perdas com clientes";

export const REGIMES_TRIBUTARIOS = ["SIMPLES_NACIONAL", "LUCRO_PRESUMIDO", "OUTRO"] as const;
export type RegimeTributario = (typeof REGIMES_TRIBUTARIOS)[number];
export const REGIME_LABELS: Record<RegimeTributario, string> = {
  SIMPLES_NACIONAL: "Simples Nacional",
  LUCRO_PRESUMIDO: "Lucro presumido",
  OUTRO: "Outro",
};

export const CUSTOS_DO_MATERIAL = ["FICHA", "COMPRAS"] as const;
export type CustoDoMaterial = (typeof CUSTOS_DO_MATERIAL)[number];

export const configuracaoFinanceiraSchema = z.object({
  dreLigado: z.boolean(),
  regime: z.enum(REGIMES_TRIBUTARIOS).nullable(),
  /** Centésimos de ponto: 600 = 6%. Até 30%. */
  aliquotaSobreFaturamentoBps: z.number().int().min(0).max(3000).nullable(),
  custoDoMaterial: z.enum(CUSTOS_DO_MATERIAL),
  provisionarFolha: z.boolean().optional(),
  /** Taxa da provisão de 13º e férias sobre os salários, até 50%. */
  taxaDeProvisaoBps: z.number().int().min(0).max(5000).optional(),
  aliquotaSobreLucroBps: z.number().int().min(0).max(3000).nullable().optional(),
  /** Taxa do cartão sobre o recebido, até 20%. */
  taxaDoCartaoBps: z.number().int().min(0).max(2000).nullable().optional(),
  tarifaDoBoletoCents: z.number().int().min(0).max(100_000).nullable().optional(),
});

/** Categoria das taxas automáticas de recebimento. */
export const CATEGORIA_DAS_TAXAS = "Taxas de cartão e boleto";

/** A taxa de um recebimento pela forma de pagamento: cartão em %, boleto em valor fixo. */
export function taxaDoRecebimento(valorCents: number, forma: string | null | undefined, c: { taxaDoCartaoBps?: number | null; tarifaDoBoletoCents?: number | null }): number {
  if (forma === "CARTAO" && c.taxaDoCartaoBps) return Math.round((valorCents * c.taxaDoCartaoBps) / 10_000);
  if (forma === "BOLETO" && c.tarifaDoBoletoCents) return Math.min(c.tarifaDoBoletoCents, valorCents);
  return 0;
}
export type ConfiguracaoFinanceiraInput = z.infer<typeof configuracaoFinanceiraSchema>;
